import type { CrucibleSessionConnection } from '@sorrem/shared';
import { CRUCIBLE_JOIN_SESSION_KEY } from '@sorrem-client/ui/shared/browser-storage';

/**
 * The run connection stored when the host became ready (see
 * party-realtime-store.ts), so a reload can rejoin. This module owns the
 * record: it is written, read back and forgotten only here.
 */
export interface StoredCrucibleSessionConnection {
    crucibleId: string;
    sessionConnection: CrucibleSessionConnection;
}

/** Stores the run connection; unavailable storage is ignored, as the page still has it in memory. */
export function setStoredCrucibleJoinSession(stored: StoredCrucibleSessionConnection): void {
    try {
        window.sessionStorage.setItem(CRUCIBLE_JOIN_SESSION_KEY, JSON.stringify(stored));
    } catch {
        // Storage blocked or full: only a reload rejoin is lost.
    }
}

/** Reads back the stored run connection, or null when there is none, it doesn't parse, or storage is unavailable. */
export function getStoredCrucibleJoinSession(): StoredCrucibleSessionConnection | null {
    try {
        // Inside the try: storage itself can throw (site data blocked).
        const raw = window.sessionStorage.getItem(CRUCIBLE_JOIN_SESSION_KEY);
        return raw ? parseStoredCrucibleJoinSession(raw) : null;
    } catch {
        return null;
    }
}

/** Forgets the stored run connection, once the player has left the run for good. */
export function clearStoredCrucibleJoinSession(): void {
    try {
        window.sessionStorage.removeItem(CRUCIBLE_JOIN_SESSION_KEY);
    } catch {
        // Storage unavailable: there is nothing stored to forget.
    }
}

/** The stored text as a run connection, or null when it is not one. Throws on text that is not JSON. */
function parseStoredCrucibleJoinSession(raw: string): StoredCrucibleSessionConnection | null {
    const parsed = JSON.parse(raw) as Partial<StoredCrucibleSessionConnection> | null;
    if (
        !parsed
        || typeof parsed !== 'object'
        || typeof parsed.crucibleId !== 'string'
        || typeof parsed.sessionConnection !== 'object'
        || parsed.sessionConnection === null
        || typeof parsed.sessionConnection.host !== 'string'
        || typeof parsed.sessionConnection.port !== 'number'
        || typeof parsed.sessionConnection.socketPath !== 'string'
    ) {
        return null;
    }

    return { crucibleId: parsed.crucibleId, sessionConnection: parsed.sessionConnection };
}
